import { errorForm, task, ITaskData, logger } from '@origin-digital/reporting-client';
import { CustomerCodeEnum, ISharedAppContextV2 } from '@origin-digital/shared-app-context';

import {
  trackingAppId,
  divisionIdLabelMap,
  trackingAppIdAuth,
  IDivisionIdWithLpg,
} from 'src/consts';
import { IAppContext } from 'src/context/AppContext';
import { isMyAccount } from 'src/utils/helpers';

type IPaymentType = 'full' | 'partial' | 'other';
interface IFormData {
  amount?: string;
  amountDue?: string;
  divisionId?: IDivisionIdWithLpg | '';
  receiptNumber?: string;
}

export const getTrackingAppId = () => (isMyAccount() ? trackingAppIdAuth : trackingAppId);

export const getTaskData = (
  action: ITaskData['action'],
  divisionId: IDivisionIdWithLpg | '',
): Partial<ITaskData> => {
  const divisionLabel = divisionIdLabelMap[divisionId] || 'Other';

  return {
    action,
    divisionCode: divisionLabel.toLowerCase().replace(' ', '-'),
    paymentMethod: 'card',
    formName: getTrackingAppId(),
  } as ITaskData;
};

export const getAuthTaskData = (
  sharedAppContext?: ISharedAppContextV2,
): Pick<ITaskData, 'user'> => {
  if (!sharedAppContext || !sharedAppContext.businessPartnerId) {
    return {};
  }
  let customerCode;
  switch (sharedAppContext.customerCode) {
    case CustomerCodeEnum.RESI:
      customerCode = CustomerCodeEnum.RESI;
      break;
    case CustomerCodeEnum.SME:
      customerCode = CustomerCodeEnum.SME;
      break;
    case CustomerCodeEnum.MSP:
      customerCode = CustomerCodeEnum.MSP;
      break;
    default:
      customerCode = CustomerCodeEnum.RESI;
      break;
  }
  return {
    user: {
      businessPartner: sharedAppContext.businessPartnerId,
      customerType: customerCode,
    },
  };
};

export const getPaymentType = ({ amount, amountDue }: IFormData): IPaymentType => {
  if (!amount || !amountDue) {
    return;
  }

  const numberAmount = parseFloat(amount);
  const numberAmountDue = parseFloat(amountDue);
  if (numberAmount === numberAmountDue) {
    return 'full';
  } else if (numberAmount < numberAmountDue) {
    return 'partial';
  }
  return 'other';
};

export const errorTracking = (error: Error): void => {
  logger.warn('errorTracking()', error);

  errorForm({
    errorCode: error.name,
    errorMessage: error.message,
    formName: getTrackingAppId(),
  });
};

export const formStart = (
  { context, setContext }: IAppContext,
  sharedAppContext?: ISharedAppContextV2,
) => {
  if (context.started) {
    return;
  }

  const taskData: ITaskData = {
    action: 'start',
    formName: getTrackingAppId(),
    ...getAuthTaskData(sharedAppContext),
  } as ITaskData;

  task.base(getTrackingAppId(), taskData);

  setContext({
    started: true,
  });
};

export const formUpdate = ({ amount, amountDue, divisionId }: IFormData) => {
  const taskData: ITaskData = {
    ...getTaskData('update', divisionId),
    amountDue,
    paymentType: getPaymentType({
      amountDue,
      amount,
    }),
  } as ITaskData;

  task.base(getTrackingAppId(), taskData);
};

export const formComplete = (
  { amount, amountDue, divisionId, receiptNumber }: IFormData,
  sharedAppContext: ISharedAppContextV2,
) => {
  const taskData: ITaskData = {
    ...getTaskData('complete', divisionId),
    amountDue,
    amountPaid: amount,
    receiptNumber,
    paymentType: getPaymentType({
      amountDue,
      amount,
    }),
    ...getAuthTaskData(sharedAppContext),
  } as ITaskData;

  task.base(getTrackingAppId(), taskData);
};
